import React, { useRef, useEffect, useState } from "react"
import { useAccessContext } from "@/contexts/AccessContext"
import { polygonLabelPoint } from "@/utils/polygonLabelPoint"
import InfoIcon from "@mui/icons-material/Info"
import ReactDOMServer from "react-dom/server"
import { useMap } from "react-leaflet"
import GeoJsonLayer from "@/components/map/GeoJsonLayer"
import { useGisSuppressed } from "@/features/gis/GisInteractivityContext.jsx"
import L from "leaflet"
import "leaflet.markercluster"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import { coreGet } from "@/services/coreApi"

/**
 * Filters GeoJSON features to only those visible within the current map viewport.
 * @param {import("leaflet").Map} map - Leaflet map instance to determine current bounds.
 * @param {Array<import("geojson").Feature>} features - Array of GeoJSON features to filter.
 * @returns {Array<import("geojson").Feature>} Filtered features that intersect the viewport.
 */
function filterViewportBoundingbox(map, features) {
  const bounds = map.getBounds()
  const filteredFeatures = features.filter((feature) => {
    const geom = feature.geometry
    if (geom.type === "LineString") {
      return geom.coordinates.some((coord) => bounds.contains([coord[1], coord[0]]))
    } else if (geom.type === "MultiLineString") {
      return geom.coordinates.some((line) => line.some((coord) => bounds.contains([coord[1], coord[0]])))
    } else if (geom.type === "Point") {
      return bounds.contains([geom.coordinates[1], geom.coordinates[0]])
    } else if (geom.type === "Polygon") {
      return geom.coordinates.some((ring) => ring.some((coord) => bounds.contains([coord[1], coord[0]])))
    } else {
      if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        return bounds.contains([geom.coordinates[1], geom.coordinates[0]])
      }
      return false
    }
  })
  return filteredFeatures
}

export default function Regions({
  cwRegionGeojson,
  setCwRegionGeojson,
  toggleFlags,
  zoomLevel,
  dragged,
  resized,
  switchGisLayersActive,
}) {
  const { accessContext, setAccessContext } = useAccessContext()
  const map = useMap()
  const suppressed = useGisSuppressed()
  const markerRefs = useRef([])
  const clusterRef = useRef(null)
  const clearedThisCycleRef = useRef(false)
  const addedKeysRef = useRef(new Set())
  const [clusterVersion, setClusterVersion] = useState(0)
  const regionsEnabled = switchGisLayersActive && toggleFlags.includes("Regions")

  useEffect(() => {
    if (!regionsEnabled) return
    if (cwRegionGeojson !== null) return

    let cancelled = false
    const load = async () => {
      try {
        const data = await coreGet("/geo/regions", accessContext, setAccessContext, ".core /geo/regions error=")
        if (!cancelled) {
          setCwRegionGeojson(data)
        }
      } catch (error) {
        console.error(error)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [accessContext, cwRegionGeojson, regionsEnabled, setAccessContext, setCwRegionGeojson])

  // Create/remove marker cluster when Regions or GIS toggle changes; clean up previous cluster
  useEffect(() => {
    if (clusterRef.current) {
      clusterRef.current.clearLayers()
      map.removeLayer(clusterRef.current)
      clusterRef.current = null
    }
    if (regionsEnabled) {
      clusterRef.current = L.markerClusterGroup({
        chunkedLoading: true,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        maxClusterRadius: (zoom) => Math.max(60 - (zoom - 6) * 6, 20),
        disableClusteringAtZoom: 11,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount()
          return L.divIcon({
            html: `<div style="
              background: rgba(0, 0, 255, 0.7);
              color: white;
              border-radius: 50%;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 0 0 6px rgba(0, 0, 255, 0.2);
              font-weight: 500;
              font-size: 14px;
            ">${count}</div>`,
            className: "custom-cluster-icon",
            iconSize: [40, 40],
            iconAnchor: [20, 20],
          })
        },
      })
      clusterRef.current.addTo(map)
      setClusterVersion((v) => v + 1)
    }
    return () => {
      if (clusterRef.current) {
        clusterRef.current.clearLayers()
        map.removeLayer(clusterRef.current)
        clusterRef.current = null
      }
    }
  }, [map, regionsEnabled, toggleFlags, switchGisLayersActive])

  // Reset per-cycle flags whenever inputs change (forces fresh rebuild of cluster contents)
  useEffect(() => {
    clearedThisCycleRef.current = false
    addedKeysRef.current = new Set()
  }, [zoomLevel, dragged, resized, cwRegionGeojson, toggleFlags, switchGisLayersActive])

  // Ensure cluster is removed when Regions toggle is off or GIS layers are globally disabled
  useEffect(() => {
    if (!regionsEnabled) {
      if (clusterRef.current) {
        clusterRef.current.clearLayers()
        map.removeLayer(clusterRef.current)
        clusterRef.current = null
      }
    }
  }, [map, regionsEnabled])

  // Remove rendered markers when Regions or GIS is turned off
  useEffect(() => {
    if (!regionsEnabled) {
      markerRefs.current.forEach((marker) => marker.remove())
      markerRefs.current = []
    }
  }, [regionsEnabled])

  // Release the regions dataset when the layer is not active.
  useEffect(() => {
    if (!regionsEnabled && cwRegionGeojson !== null) {
      setCwRegionGeojson(null)
    }
  }, [cwRegionGeojson, regionsEnabled, setCwRegionGeojson])

  return !regionsEnabled || cwRegionGeojson === null ? null : (
    <GeoJsonLayer
      key={[zoomLevel, dragged, resized, clusterVersion, suppressed]}
      data={filterViewportBoundingbox(map, cwRegionGeojson.features)}
      style={{ fillColor: "blue", color: "blue", dashArray: "5,5", interactive: false }}
      onEachFeature={(feature, layer) => {
        if (clusterRef.current && !clearedThisCycleRef.current) {
          clusterRef.current.clearLayers()
          markerRefs.current = []
          clearedThisCycleRef.current = true
        }

        const getFeatureKey = (feat, ctr) => {
          const fid = feat?.id || feat?.properties?.id || feat?.properties?.uuid || feat?.properties?.gid
          if (fid != null) return String(fid)
          if (ctr) {
            return `${feat?.geometry?.type || "geom"}:${ctr.lat.toFixed(6)},${ctr.lng.toFixed(6)}:${
              feat?.properties?.info || ""
            }`
          }
          return JSON.stringify(feat?.geometry || {})
        }

        const popupContent = `<div><strong>${feature.properties.info}</strong>${
          feature.properties.id ? `<br/>ID: ${feature.properties.id}` : ""
        }</div>`

        let center = null
        // Visuelles Zentrum (pole of inaccessibility) — liegt gut sichtbar im Polygon, Bbox-Center oft daneben
        const labelPt = polygonLabelPoint(feature)
        if (labelPt) {
          const [lng, lat] = labelPt
          center = L.latLng(lat, lng)
        }
        if (!center && typeof layer.getLatLng === "function") {
          const ll = layer.getLatLng()
          center = L.latLng(ll.lat, ll.lng)
        } else if (!center && typeof layer.getBounds === "function") {
          try {
            center = layer.getBounds().getCenter()
          } catch {}
        }
        if (!center && feature?.geometry?.type === "Point" && Array.isArray(feature.geometry.coordinates)) {
          const [lng, lat] = feature.geometry.coordinates
          center = L.latLng(lat, lng)
        }

        const fKey = getFeatureKey(feature, center)
        if (addedKeysRef.current.has(fKey)) {
          return
        }
        addedKeysRef.current.add(fKey)

        if (center && clusterRef.current) {
          const iconHtml = ReactDOMServer.renderToStaticMarkup(
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <InfoIcon style={{ color: "blue" }} />
            </div>
          )

          const customIcon = L.divIcon({
            html: iconHtml,
            className: "custom-info-icon",
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          })

          const marker = L.marker(center, { icon: customIcon, interactive: !suppressed })
          if (!suppressed) marker.bindPopup(popupContent, { autoPan: false })
          clusterRef.current.addLayer(marker)
          markerRefs.current.push(marker)
        }
      }}
      transparent
    />
  )
}
